// ── Mandar una plantilla cuando la ventana cerró ─────────────────────────────
// 2026-09-16.
//
// Es el único camino para escribirle a alguien pasadas las 24 horas, y ESTE
// ENVÍO SE COBRA. De ahí las dos decisiones de esta pantalla:
//
//  1. Se ve el mensaje ARMADO, con los datos puestos, antes de mandarlo. Una
//     plantilla con {{1}} sin rellenar no se parece a lo que la persona va a
//     leer, y lo que se revisa es lo que llega.
//  2. El botón dice que cuesta. No en letra chica: si el primero que lo toca
//     no sabía que se cobraba, la que se entera es la factura.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Pressable } from '../ui/Pressable.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import { useToast } from '../ui/Toast.js';
import { useSettingsTabLabel } from '../ui/SettingsLink.js';
import { ApiError } from '../../lib/api.js';
import {
  usePlantillasEnviables, useEnviarPlantilla, type PlantillaEnviable,
} from '../../hooks/useWhatsappTemplates.js';

export interface EnviarPlantillaProps {
  open: boolean;
  onClose: () => void;
  threadId: number;
  /** Para encabezar el diálogo con a quién se le va a escribir. */
  destinatario: string;
}

/** El texto con los datos puestos. Es lo que la persona va a leer.
 *
 *  `split().join()` y no `replaceAll`: el paquete web compila a una versión de
 *  JS que no la tiene. Un hueco todavía vacío se deja como está, para que la
 *  vista previa muestre qué falta en vez de un agujero. */
function armar(body: string, valores: string[]): string {
  return valores.reduce(
    (acc, v, i) => (v ? acc.split(`{{${i + 1}}}`).join(v) : acc),
    body,
  );
}

export function EnviarPlantilla({ open, onClose, threadId, destinatario }: EnviarPlantillaProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const tabCanales = useSettingsTabLabel('channels');
  const { data, isLoading } = usePlantillasEnviables(open);
  const enviar = useEnviarPlantilla();

  const [elegida, setElegida] = useState<PlantillaEnviable | null>(null);
  const [valores, setValores] = useState<string[]>([]);

  // Cada apertura arranca limpia: quedarse con la plantilla de la vez pasada
  // haría que un clic distraído le mande a otra persona lo que no es.
  useEffect(() => {
    if (!open) return;
    setElegida(null);
    setValores([]);
  }, [open]);

  const plantillas = data?.items ?? [];
  const listo = elegida != null && valores.length === elegida.variables.length
    && valores.every(v => v.trim().length > 0);

  const previsualizacion = useMemo(
    () => (elegida ? armar(elegida.body, valores) : ''),
    [elegida, valores],
  );

  function elegir(p: PlantillaEnviable) {
    setElegida(p);
    setValores(p.variables.map(() => ''));
  }

  function mandar() {
    if (!elegida || !listo) return;
    enviar.mutate(
      { threadId, templateId: elegida.id, valores },
      {
        onSuccess: () => { toast.success(t('sendTemplate.sent')); onClose(); },
        onError: (err: unknown) => toast.error(err instanceof ApiError ? err.message : String(err)),
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('sendTemplate.title', { name: destinatario })}
      description={t('sendTemplate.hint')}
      size="md"
    >
      {isLoading ? (
        <LoadingState size="sm" />
      ) : plantillas.length === 0 ? (
        // Sin plantillas aprobadas no hay nada que hacer desde acá, y decir
        // dónde se crean evita la búsqueda a ciegas por Ajustes.
        <EmptyState
          title={t('sendTemplate.noneTitle')}
          description={t('sendTemplate.noneHint', { tab: tabCanales })}
        />
      ) : elegida == null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          {plantillas.map(p => (
            <Pressable key={p.id} onPress={() => elegir(p)} fullWidth ariaLabel={p.title}>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)',
                padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)',
                background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
                textAlign: 'left',
              }}>
                <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)' }}>{p.title}</span>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', whiteSpace: 'pre-wrap' }}>
                  {p.body}
                </span>
              </div>
            </Pressable>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
          <Pressable onPress={() => setElegida(null)} ariaLabel={t('sendTemplate.back')}>
            <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-cyan)' }}>
              ← {t('sendTemplate.back')}
            </span>
          </Pressable>

          {elegida.variables.map((nombre, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
              <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {nombre}
              </span>
              <Input
                value={valores[i] ?? ''}
                onChange={v => setValores(vs => { const c = [...vs]; c[i] = v; return c; })}
                placeholder={nombre}
              />
            </div>
          ))}

          {/* Lo que va a leer la persona. Se revisa esto, no la plantilla. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t('sendTemplate.preview')}
            </span>
            <div style={{
              padding: 'var(--evo-chat-bubble-py) var(--evo-chat-bubble-px)',
              borderRadius: 'var(--evo-chat-bubble-r)',
              background: 'var(--evo-chat-bubble-out-bg)',
              fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)',
              whiteSpace: 'pre-wrap', lineHeight: 1.6,
            }}>
              {previsualizacion}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-3)', alignItems: 'center' }}>
            <Button variant="ghost" tone="muted" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid" tone="cyan" size="sm"
              disabled={!listo}
              loading={enviar.isPending}
              onClick={mandar}
            >
              {t('sendTemplate.send')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
